import { Card, Flex, Heading, Text, Box, Button, Separator, TextField, Container, Badge } from '@radix-ui/themes';
import React, { useState, useEffect } from 'react';
import axiosInstance from '../config/axiosConfig';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { passwordRules } from '../Schema/index.js';

// Validation schema for password change form
const passwordSchema = Yup.object({
  currentPassword: Yup.string()
    .required('Current password is required'),
  newPassword: Yup.string()
    .matches(passwordRules, { message: 'Please create a stronger password (at least one lowercase, uppercase, digit, and special character)' })
    .required('Required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your new password'),
});

// CSS styles
const styles = `
  .input-error .rt-TextFieldInput {
    border-color: red;
  }
  
  .profile-card {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  .profile-section {
    background-color: #f9fafb;
    border-radius: 8px;
    padding: 16px;
  }
  
  .form-field-container {
    margin-bottom: 16px;
  }
  
  .password-requirements {
    font-size: 12px;
    color: #666;
    margin-top: 4px;
  }
`;

const ProfilePage = () => {
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Formik setup for password change form
  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      try {
        const response = await axiosInstance.post('/Account/update-password', {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });

        if (response.data.result) {
          toast.success('Password changed successfully');
          setIsChangingPassword(false);
          resetForm();
        } else {
          toast.error(response.data.errorResponse?.message || 'Failed to change password');
        }
      } catch (error) {
        console.error('Error changing password:', error);
        toast.error(error.response?.data?.errorResponse?.message || 'Error changing password');
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/User/get-profile');
      if (response.data.result?.userGetList?.length > 0) {
        setProfileData(response.data.result.userGetList[0]);
      } else {
        toast.error('Failed to load profile data');
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast.error('Error loading profile data');
    } finally {
      setIsLoading(false);
    }
  };

  const renderProfileField = (label, value) => {
    // Only render fields that have values
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return (
      <Flex direction="row" gap="2" my="3">
        <Text size="2" weight="medium" style={{ width: '150px', color: '#444' }}>
          {label}
        </Text>
        <Text size="3" weight="medium">
          {value}
        </Text>
      </Flex>
    );
  };

  if (isLoading) {
    return (
      <Flex height="100%" justify="center" align="center" p="6">
        <Text size="3">Loading profile data...</Text>
      </Flex>
    );
  }

  return (
    <Container size="3">
      <style>{styles}</style>
      <ToastContainer position="top-right" autoClose={3000} />
      
      <Card size="3" className="profile-card" mt="4">
        <Flex direction="column" gap="6">
          <Flex justify="between" align="center" mb="2">
            <Heading size="5" style={{ color: '#1a3d7c' }}>User Profile</Heading>
            <Button 
              color={isChangingPassword ? "gray" : "green"}
              onClick={() => setIsChangingPassword(!isChangingPassword)} 
              size="2"
              variant={isChangingPassword ? "soft" : "solid"}
            >
              {isChangingPassword ? 'Cancel' : 'Change Password'}
            </Button>
          </Flex>
          
          {profileData && (
            <Box className="profile-section">
              <Heading size="3" mb="4" style={{ color: '#444' }}>Personal Information</Heading>
              <Flex direction="column" gap="2">
                <Flex direction={{ initial: 'column', sm: 'row' }} gap="4" wrap="wrap">
                  <Box style={{ flex: 1, minWidth: '200px' }}>
                    {renderProfileField('Full Name', `${profileData.firstName || ''} ${profileData.lastName || ''}`)}
                    {renderProfileField('Email', profileData.email)}
                    {renderProfileField('Username', profileData.userName)}
                    {profileData.isActive !== undefined && (
                      <Flex direction="row" gap="2" my="3">
                        <Text size="2" weight="medium" style={{ width: '150px', color: '#444' }}>Status</Text>
                        <Badge color={profileData.isActive ? "green" : "red"} variant="soft">
                          {profileData.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Flex>
                    )}
                  </Box>
                  <Box style={{ flex: 1, minWidth: '200px' }}>
                    {renderProfileField('Employee ID', profileData.employeeId)}
                    {renderProfileField('Role', profileData.role)}
                    {renderProfileField('Department', profileData.depId)}
                    {renderProfileField('Unit', profileData.unitName)}
                  </Box>
                </Flex>
                {renderProfileField('Department Location', profileData.depLocation)}
                {renderProfileField('Supervisor', profileData.supervisor)}
              </Flex>
            </Box>
          )}

          {isChangingPassword && (
            <Box className="profile-section" mt="4">
              <Heading size="3" mb="4" style={{ color: '#444' }}>Change Password</Heading>
              
              <Box as="form" onSubmit={formik.handleSubmit}>
                <Flex direction="column" gap="4">
                  <Box className="form-field-container">
                    <Text as="label" size="2" weight="medium" mb="2" display="block">
                      Current Password
                    </Text>
                    <TextField.Root
                      name="currentPassword"
                      type="password"
                      placeholder="Enter your current password"
                      value={formik.values.currentPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={formik.errors.currentPassword && formik.touched.currentPassword ? "input-error" : ""}
                    />
                    {formik.errors.currentPassword && formik.touched.currentPassword && (
                      <Text size="2" color="red" mt="1">
                        {formik.errors.currentPassword}
                      </Text>
                    )}
                  </Box>
                  
                  <Box className="form-field-container">
                    <Text as="label" size="2" weight="medium" mb="2" display="block">
                      New Password
                    </Text>
                    <TextField.Root
                      name="newPassword"
                      type="password"
                      placeholder="Enter your new password"
                      value={formik.values.newPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={formik.errors.newPassword && formik.touched.newPassword ? "input-error" : ""}
                    />
                    {formik.errors.newPassword && formik.touched.newPassword ? (
                      <Text size="2" color="red" mt="1">
                        {formik.errors.newPassword}
                      </Text>
                    ) : (
                      <Text className="password-requirements">
                        Password must contain at least one lowercase letter, one uppercase letter, one digit, and one special character.
                      </Text>
                    )}
                  </Box>
                  
                  <Box className="form-field-container">
                    <Text as="label" size="2" weight="medium" mb="2" display="block">
                      Confirm New Password
                    </Text>
                    <TextField.Root
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm your new password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={formik.errors.confirmPassword && formik.touched.confirmPassword ? "input-error" : ""}
                    />
                    {formik.errors.confirmPassword && formik.touched.confirmPassword && (
                      <Text size="2" color="red" mt="1">
                        {formik.errors.confirmPassword}
                      </Text>
                    )}
                  </Box>
                  
                  <Flex justify="end" mt="4">
                    <Button 
                      type="submit" 
                      color="green" 
                      disabled={formik.isSubmitting || !formik.isValid}
                      size="2"
                    >
                      {formik.isSubmitting ? 'Updating...' : 'Update Password'}
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            </Box>
          )}
        </Flex>
      </Card>
    </Container>
  );
};

export default ProfilePage;